import { Descope, useSession } from '@descope/react-sdk';
import { useStatsigClient } from '@statsig/react-bindings';
import { type ReactNode, useState } from 'react';
import { PortalBanner } from '@components/portal-banner/portal-banner';
import { GlobalSpinner } from '@components/sds/global-spinner/global-spinner';
import { useLoggedInUser } from '@services/auth/use-logged-in-user';
import styles from './descope-wrapper.module.scss';

interface DescopeConfig {
  flowId: string;
  styleId?: string;
  tenant?: string;
}

interface DescopeWrappedAppProps {
  readonly config: DescopeConfig;
  readonly children: ReactNode;
}

export const DescopeWrappedApp = ({
  children,
  config: { flowId, styleId, tenant } = { flowId: '' },
}: DescopeWrappedAppProps) => {
  const { isAuthenticated } = useSession();
  const { logEvent } = useStatsigClient();
  const [isInitializing, setIsInitializing] = useState(true);

  // TODO: Remove old `isLoggedIn` check - currently needed for test environments
  const { isLoggedIn, isFetching } = useLoggedInUser();

  if (isLoggedIn) {
    return children;
  }

  const searchParameters = new URLSearchParams(window.location.search);

  // To allow for temporary override of flow, tenant & style settings for testing
  const flowIdOverride =
    searchParameters.get('flowId') ?? localStorage.getItem('flowId');
  const styleIdOverride =
    searchParameters.get('styleId') ?? localStorage.getItem('styleId');
  const tenantOverride =
    searchParameters.get('tenant') ??
    localStorage.getItem('tenant') ??
    sessionStorage.getItem('tenant');

  const actualFlowId = flowIdOverride ?? flowId;

  // Clean the URL search params to avoid redirect loops
  searchParameters.delete('code');
  searchParameters.delete('descope-login-flow');
  searchParameters.delete('t'); // token param
  searchParameters.delete('sso');

  // Update the URL with the cleaned search params
  const redirectUrl = new URL(window.location.href);
  redirectUrl.search = searchParameters.toString();

  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <GlobalSpinner isVisible={isInitializing || isFetching} />
          <Descope
            flowId={actualFlowId}
            styleId={styleIdOverride ?? styleId}
            tenant={tenantOverride ?? tenant ?? ''}
            redirectUrl={redirectUrl.toString()}
            onReady={() => {
              setIsInitializing(false);
            }}
            onSuccess={event_ => {
              const userId = event_.detail?.user?.userId;
              const userTenants = event_.detail?.user?.userTenants
                ?.map(t => t.tenantName)
                .join(',');

              logEvent('portal_wrapper_login_succeeded', undefined, {
                flowId: actualFlowId,
                userId: userId ?? '',
                userTenants: userTenants ?? '',
              });

              console.debug('Login succeeded', {
                flowId: actualFlowId,
                userId,
              });
              // Clear stale inactivity timestamp from previous sessions
              localStorage.removeItem('lastActivityTimestamp');
            }}
            onError={event_ => {
              const errorCode = event_.detail?.errorCode;
              const errorDescription = event_.detail?.errorDescription;
              const errorMessage = event_.detail?.errorMessage;

              logEvent('portal_wrapper_login_failed', undefined, {
                flowId: actualFlowId,
                errorCode: errorCode ?? '',
                errorDescription: errorDescription ?? '',
                errorMessage: errorMessage ?? '',
              });

              console.debug('Login failed', {
                errorCode,
                errorDescription,
                errorMessage,
                flowId: actualFlowId,
              });
            }}
          />
        </div>
        <PortalBanner />
      </div>
    );
  }

  return children;
};
